import type { PromotionEffect } from '../schemas/promotion.js';

/**
 * Anti-undercut guard for promotions (gap G7).
 *
 * An operator could price a promotion arbitrarily low with nothing objecting.
 * The failure is not dramatic — nobody notices a 90%-off promotion until the
 * month's takings come in — which is exactly why it needs a guard at the moment
 * of saving rather than a report afterwards.
 *
 * **This is a floor on price retained, not a true cost margin.** The catalogue
 * has no cost field: nobody has ever asked an operator what a wash costs them
 * to deliver, and inventing a cost model they never entered would produce a
 * number that looks authoritative and means nothing. `minMarginPct` is
 * therefore "the promotion must leave at least N% of list price standing" —
 * which is what the roadmap always called it, "anti-undercut".
 *
 * Default off (`0`), per the standing rule that every tunable ships at zero
 * until the owner sets it. A guard that fires on day one with a number nobody
 * chose gets dismissed on reflex, and then it is furniture.
 */

/** What a promotion does to one item's price, and whether that is acceptable. */
export interface MarginAssessment {
  /** Sen the customer would pay after the promotion. */
  readonly discountedSen: number;
  /** Percentage of the list price still standing, 0–100, rounded. */
  readonly retainedPct: number;
  /** Lowest price the floor permits, in sen. */
  readonly floorSen: number;
  /** True when the promotion prices below the floor. */
  readonly belowFloor: boolean;
  /** False when no floor is configured — the guard is off. */
  readonly enforced: boolean;
}

/**
 * Apply one promotion effect to a list price.
 *
 * Returns the list price unchanged for effects that do not reduce THIS item —
 * a free-service or replace-price effect targets a specific id, and judging it
 * against an unrelated item's list price would produce a confident wrong
 * answer. Pure and total: junk in yields the list price, never NaN.
 */
export function applyEffectSen(listPriceSen: number, effect: PromotionEffect): number {
  const list = Number.isFinite(listPriceSen) && listPriceSen > 0 ? Math.trunc(listPriceSen) : 0;
  if (list === 0) return 0;

  switch (effect.type) {
    case 'pct_off': {
      const pct = Number.isFinite(effect.value) ? Math.min(100, Math.max(0, effect.value)) : 0;
      return Math.max(0, Math.round(list * (1 - pct / 100)));
    }
    case 'fixed_off': {
      const off = Number.isFinite(effect.value) ? Math.max(0, Math.trunc(effect.value)) : 0;
      return Math.max(0, list - off);
    }
    case 'replace_price': {
      const p = Number.isFinite(effect.newPrice) ? Math.max(0, Math.trunc(effect.newPrice)) : 0;
      return p;
    }
    case 'free_service':
      // Targets a named service; against an arbitrary list price it says nothing.
      return list;
    default:
      return list;
  }
}

/**
 * Judge one promotion against the tenant's floor.
 *
 * `minMarginPct` of 0 (or absent) means the guard is off, and `enforced` says
 * so — the caller must not render a warning that reads as "this is fine" when
 * nothing was actually checked.
 */
export function assessPromotionMargin(
  listPriceSen: number,
  effect: PromotionEffect,
  minMarginPct: number | undefined,
): MarginAssessment {
  const list = Number.isFinite(listPriceSen) && listPriceSen > 0 ? Math.trunc(listPriceSen) : 0;
  const discountedSen = applyEffectSen(list, effect);

  const pct =
    typeof minMarginPct === 'number' && Number.isFinite(minMarginPct)
      ? Math.min(100, Math.max(0, minMarginPct))
      : 0;

  const enforced = pct > 0 && list > 0;
  const floorSen = enforced ? Math.ceil((list * pct) / 100) : 0;
  const retainedPct = list > 0 ? Math.round((discountedSen / list) * 100) : 0;

  return {
    discountedSen,
    retainedPct,
    floorSen,
    belowFloor: enforced && discountedSen < floorSen,
    enforced,
  };
}
